import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { ArrowsLeftRight, Graph, PaperPlaneTilt, Receipt, Ticket, X, type Icon } from '@phosphor-icons/react';
import { useConfig } from '../lib/queries';
import { useLang, useT } from '../lib/i18n';
import { navigate } from '../lib/router';
import { haptic } from '../lib/telegram';

type Tone = 'blue' | 'dark' | 'green';

interface Story {
  id: string;
  tone: Tone;
  Icon: Icon;
  title: string;
  body: string;
  cta: string;
  to: string;
}

const DURATION = 6000;
const SEEN_KEY = 'onyx.storiesSeen';

function useStories(): Story[] {
  const lang = useLang();
  const config = useConfig();
  const fee = (config.data?.swapFeePercent ?? '0.5').replace('.', lang === 'ru' ? ',' : '.');
  if (lang === 'en') {
    return [
      { id: 'networks', tone: 'blue', Icon: Graph, title: 'USDT on 9 networks', body: 'Top up USDT from TRON, TON, Ethereum, BNB Chain, Solana and 4 more networks. It all adds up to one USDT balance.', cta: 'Deposit USDT', to: '/deposit/USDT' },
      { id: 'cheques', tone: 'dark', Icon: Ticket, title: 'Cheques for any chat', body: 'Create a cheque and drop it into a DM, group or channel. The recipient claims it in one tap, even without an Onyx wallet yet.', cta: 'Create a cheque', to: '/pay/cheques/new' },
      { id: 'swap', tone: 'green', Icon: ArrowsLeftRight, title: 'Swap in seconds', body: `Swap any coin for any other at the market rate. The ${fee}% fee is shown before you confirm.`, cta: 'Swap', to: '/swap' },
      { id: 'free', tone: 'blue', Icon: PaperPlaneTilt, title: 'Free transfers', body: 'Send to an @username or to another Onyx user’s address: instant and free.', cta: 'Send', to: '/send' },
      { id: 'invoices', tone: 'dark', Icon: Receipt, title: 'Invoices for business', body: 'Issue an invoice for any amount and get paid straight to your balance. Share the link in any chat.', cta: 'Create invoice', to: '/pay/invoices/new' },
    ];
  }
  return [
    { id: 'networks', tone: 'blue', Icon: Graph, title: 'USDT в 9 сетях', body: 'Пополняйте USDT из TRON, TON, Ethereum, BNB Chain, Solana и ещё 4 сетей — на балансе всё сложится в один USDT.', cta: 'Пополнить USDT', to: '/deposit/USDT' },
    { id: 'cheques', tone: 'dark', Icon: Ticket, title: 'Чеки в любой чат', body: 'Создайте чек и отправьте его в личку, группу или канал. Получатель заберёт деньги в один тап — даже если у него ещё нет Onyx.', cta: 'Создать чек', to: '/pay/cheques/new' },
    { id: 'swap', tone: 'green', Icon: ArrowsLeftRight, title: 'Обмен за секунды', body: `Меняйте любую монету на любую по рыночному курсу. Комиссия ${fee}% видна до подтверждения.`, cta: 'Обменять', to: '/swap' },
    { id: 'free', tone: 'blue', Icon: PaperPlaneTilt, title: 'Переводы бесплатно', body: 'Отправляйте по @username или на адрес другого пользователя Onyx — мгновенно и без комиссии.', cta: 'Отправить', to: '/send' },
    { id: 'invoices', tone: 'dark', Icon: Receipt, title: 'Счета для бизнеса', body: 'Выставьте счёт на любую сумму и получите оплату прямо на баланс. Ссылку можно отправить в любой чат.', cta: 'Выставить счёт', to: '/pay/invoices/new' },
  ];
}

/* Seen state is a per-viewer convenience; losing it only re-rings the tiles. */
let seen: string[] = (() => {
  try {
    return JSON.parse(localStorage.getItem(SEEN_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
})();
const seenListeners = new Set<() => void>();
function markSeen(id: string) {
  if (seen.includes(id)) return;
  seen = [...seen, id];
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    // Private mode: keep it for this session only.
  }
  seenListeners.forEach((l) => l());
}
const useSeen = () =>
  useSyncExternalStore(
    (cb) => {
      seenListeners.add(cb);
      return () => seenListeners.delete(cb);
    },
    () => seen,
  );

export function StoriesRow({ label }: { label: string }) {
  const stories = useStories();
  const seenIds = useSeen();
  const [open, setOpen] = useState<number | null>(null);
  return (
    <>
      <div className="stories" role="list" aria-label={label}>
        {stories.map((s, i) => (
          <button
            key={s.id}
            type="button"
            role="listitem"
            className={`story-tile story-tile--${s.tone}${seenIds.includes(s.id) ? ' is-seen' : ''}`}
            onClick={() => {
              haptic.tap();
              setOpen(i);
            }}
          >
            <span className="story-tile__title">{s.title}</span>
            <s.Icon className="story-tile__art" size={54} weight="fill" aria-hidden="true" />
          </button>
        ))}
      </div>
      {open !== null && <StoryViewer stories={stories} start={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function StoryViewer({ stories, start, onClose }: { stories: Story[]; start: number; onClose: () => void }) {
  const t = useT();
  const [index, setIndex] = useState(start);
  const [progress, setProgress] = useState(0);
  const paused = useRef(false);
  const story = stories[index]!;

  const go = useCallback(
    (next: number) => {
      if (next < 0) return setProgress(0);
      if (next >= stories.length) return onClose();
      haptic.select();
      setIndex(next);
      setProgress(0);
    },
    [stories.length, onClose],
  );

  useEffect(() => markSeen(story.id), [story.id]);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = now - last;
      last = now;
      if (!paused.current) {
        setProgress((p) => {
          const n = p + dt / DURATION;
          if (n >= 1) {
            queueMicrotask(() => go(index + 1));
            return 1;
          }
          return n;
        });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [index, go]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(index + 1);
      if (e.key === 'ArrowLeft') go(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, go, onClose]);

  return createPortal(
    <div className={`story-viewer story-viewer--${story.tone}`} role="dialog" aria-modal="true" aria-label={story.title}>
      <div className="story-viewer__bars" aria-hidden="true">
        {stories.map((s, i) => (
          <span key={s.id} className="story-viewer__bar">
            <span style={{ transform: `scaleX(${i < index ? 1 : i === index ? progress : 0})` }} />
          </span>
        ))}
      </div>
      <button type="button" className="story-viewer__close" aria-label={t['common.close']} onClick={onClose}>
        <X size={22} weight="bold" />
      </button>
      <div
        className="story-viewer__stage"
        onPointerDown={() => (paused.current = true)}
        onPointerUp={(e) => {
          paused.current = false;
          const { left, width } = (e.currentTarget as HTMLElement).getBoundingClientRect();
          go(e.clientX - left < width / 3 ? index - 1 : index + 1);
        }}
        onPointerCancel={() => (paused.current = false)}
      >
        <story.Icon className="story-viewer__art" size={168} weight="fill" aria-hidden="true" />
        <h2 className="story-viewer__title">{story.title}</h2>
        <p className="story-viewer__body">{story.body}</p>
      </div>
      <button
        type="button"
        className="story-viewer__cta"
        onClick={() => {
          haptic.tap();
          onClose();
          navigate(story.to);
        }}
      >
        {story.cta}
      </button>
    </div>,
    document.body,
  );
}
